import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Button, TextButton, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { BankAccountDetailsModal } from '~/BankAccountDetailsModal';
import ContactSupportButton from '~/components/ContactSupportButton';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { useStore, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { PusherEvent, useBusinessChannelEvent } from '~/pusher';
import { isBankAccountConnected } from '~/store/business-types';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import { PlaidMode, usePlaidState } from './usePlaidState';
export const GetUseConnectBankAccountTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            UseConnectBankAccountTID: {
                ConnectedBankAccountDetails: 'connected-bank-account-details',
                EnterManuallyDescription: (testid)=>testid ? "".concat(testid, "-description") : '',
                EnterManuallyButton: (testid)=>testid ? "".concat(testid, "-btn") : ''
            }
        }
    });
export const useConnectBankAccount = ()=>{
    const [plaidCanceled, setPlaidCanceled] = useState(false);
    const [isConnectingInProgress, setIsConnectingInProgress] = useState(false);
    const [authorizationError, setAuthorizationError] = useState(null);
    const overlayContext = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: hookTexts } = useLocaleKeys('hooks.connectBankAccount');
    const { api, logBI, fetchUserInfo } = useStoreActions();
    const { UseConnectBankAccountTID } = GetUseConnectBankAccountTID();
    const plaidLink = usePlaidState({
        mode: {
            type: PlaidMode.Vendor
        },
        onSuccess (_account) {
            logBI(11210);
        },
        onError (error) {
            logBI(11211);
            const payoutAuthorizationError = error.payoutAuthorizationError();
            if (payoutAuthorizationError != null) {
                setAuthorizationError(payoutAuthorizationError);
            } else {
                setPlaidCanceled(true);
            }
            setIsConnectingInProgress(false);
        },
        onExit () {
            setPlaidCanceled(true);
            setIsConnectingInProgress(false);
        }
    });
    const onVendorBankAccountConnectedPushEvent = async (_event)=>{
        await fetchUserInfo();
        setIsConnectingInProgress(false);
    };
    const onDetailsSubmit = async (param, dismissModal)=>{
        let { routingNumber, accountNumber } = param;
        logBI(11212);
        setAuthorizationError(null);
        overlayContext.startLoader();
        try {
            await api.vendor.authorizeVendorBankAccount({
                routingNumber,
                accountNumber
            });
        } catch (err) {
            const payoutAuthorizationError = new ErrorResponse(err).payoutAuthorizationError();
            if (payoutAuthorizationError != null) {
                setAuthorizationError(payoutAuthorizationError);
                dismissModal();
            } else {
                Sentry.captureException(err);
                overlayContext.showErrorToast(commonTexts.toasts.errorSubmittingBankAccountDetails(), UseConnectBankAccountTID.SubmitErrorToast());
                logBI(11213);
            }
        } finally{
            overlayContext.stopLoader();
        }
    };
    const Description = (param)=>{
        let { testId, linkTestId, className, style = {} } = param;
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": testId,
            className: className,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    style: style,
                    children: "".concat(hookTexts.addDetailsToReceivePayments(), " ")
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    style: style,
                    children: [
                        "".concat(hookTexts.noteAchFees(), " "),
                        /*#__PURE__*/ _jsx(ExternalLinkButton, {
                            href: helpArticles.paymentsViaAnchor(),
                            testId: linkTestId,
                            openInNewTab: true,
                            underline: "none",
                            bi: {
                                name: 'payments_via_anchor_learn_more'
                            },
                            children: hookTexts.learnMore()
                        })
                    ]
                })
            ]
        });
    };
    const ConnectButton = (param)=>{
        let { testId, className, enterManuallyTestId } = param;
        const userInfo = useStore((state)=>state.userInfo);
        const hasBankAccountConnected = selectors.user.hasBankAccountConnected({
            userInfo
        });
        useBusinessChannelEvent({
            source: 'mgmt',
            eventName: PusherEvent.VendorBankAccountConnected,
            callback: onVendorBankAccountConnectedPushEvent
        });
        if (hasBankAccountConnected) return /*#__PURE__*/ _jsx(_Fragment, {});
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: className,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'connect_bank_account'
                        },
                        // mui button takes up fullwidth by default when wrapped by a flex-column container, this neutralizes that
                        style: {
                            width: 'fit-content'
                        },
                        size: "medium",
                        testId: testId,
                        loading: isConnectingInProgress,
                        onClick: ()=>{
                            logBI(11204);
                            setAuthorizationError(null);
                            setIsConnectingInProgress(true);
                            plaidLink.open();
                        },
                        children: hookTexts.connectBank()
                    }),
                    plaidCanceled && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: UseConnectBankAccountTID.EnterManuallyDescription(enterManuallyTestId),
                                style: {
                                    marginTop: '12px'
                                },
                                variant: "paragraph2",
                                skin: "secondary",
                                children: hookTexts.havingTrouble()
                            }),
                            /*#__PURE__*/ _jsx(TextButton, {
                                bi: {
                                    name: 'connect_bank_manually'
                                },
                                testId: UseConnectBankAccountTID.EnterManuallyButton(enterManuallyTestId),
                                onClick: ()=>{
                                    logBI(11205);
                                    overlayContext.showModal({
                                        biName: 'bank_account_details_modal',
                                        content: (param)=>{
                                            let { dismissModal } = param;
                                            return /*#__PURE__*/ _jsx(BankAccountDetailsModal, {
                                                onSubmit: (details)=>void onDetailsSubmit(details, dismissModal),
                                                onCancel: dismissModal
                                            });
                                        }
                                    });
                                },
                                underline: "none",
                                children: hookTexts.connectUsingRouting()
                            })
                        ]
                    }),
                    authorizationError != null && /*#__PURE__*/ _jsx(Typography, {
                        testId: UseConnectBankAccountTID.AuthorizationErrorMessage(),
                        style: {
                            marginTop: '12px'
                        },
                        variant: "paragraph2",
                        skin: "error",
                        children: authorizationError === 'roleClaimMissing' ? hookTexts.authorizationSessionStale() : hookTexts.authorizationInsufficientRole()
                    })
                ]
            })
        });
    };
    const BankAccountDetails = (param)=>{
        let { payoutBankAccount, style = {} } = param;
        return isBankAccountConnected(payoutBankAccount) ? /*#__PURE__*/ _jsxs("div", {
            style: {
                display: 'flex',
                flexDirection: 'column'
            },
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    testId: UseConnectBankAccountTID.ConnectedBankAccountDetails(),
                    style: style,
                    children: hookTexts.connectedBankAccount({
                        last4: payoutBankAccount.connected.last4
                    })
                }),
                /*#__PURE__*/ _jsxs(Typography, {
                    skin: "secondary",
                    style: style,
                    children: [
                        "".concat(hookTexts.bankChangesContactSupport(), " "),
                        /*#__PURE__*/ _jsx(ContactSupportButton, {
                            underline: "none",
                            testId: "contact-support"
                        })
                    ]
                })
            ]
        }) : /*#__PURE__*/ _jsx(_Fragment, {});
    };
    return {
        Description,
        ConnectButton,
        BankAccountDetails
    };
};
